import { createSlice } from '@reduxjs/toolkit';
import { ICalendarItemData } from 'Calendar/types';
import { CALENDAR_VIEW_TYPE } from 'constants/calendar';
import { TABLE_PAGE_SIZE } from 'constants/table';
import remove from 'lodash/remove';
import moment from 'utils/moment';
import { mappingResToCalendarDataItem } from 'services/helpers';
import { IStateAppointment } from 'services/types/apt';
import { IAppointmentResItem } from 'services/types/response';
import { momentTimezone } from 'utils/time';
import actions from '../actions/apt';
import { DATE_FORMAT } from 'utils/format';

const NAME_REDUCER = 'apt';

const initialState: IStateAppointment = {
  calendar: {
    distanceTimeLine: 15,
    typeView: CALENDAR_VIEW_TYPE.DAY_VIEW,
    dateRanges: [],
    data: [],
    blockHours: [],
    params: {
      startTime: moment().format(),
    },
  },
  table: {
    data: [],
    params: {
      start_time: moment().format('YYYY-MM-DD'),
      end_time: moment().format('YYYY-MM-DD'),
      pageSize: TABLE_PAGE_SIZE,
    },
    total: 0,
    blockHours: [],
    appointmentSummaryCallerId: null
  },
  customer: {
    data: [],
    params: {
      pageSize: TABLE_PAGE_SIZE,
    },
    total: 0,
    blockHours: [],
    appointmentSummaryCallerId: null
  },
  quickBooking: {
    services: [],
  },
  activeTimeRow: '',
  staffAvailableBooking: [],
  staffAvailableBookingLoading: false,
  staffSize: 10,
  currentPage: 1,
  fullScreen: false,
  callerId: {
    params: {
      startDate: moment().format(DATE_FORMAT),
      endDate: moment().format(DATE_FORMAT),
      page: 1,
    },
    data: null,
    loading: false,
  },
  pendingCallers: {
    loading: false,
    data: [],
  },
  customerRecent: {
    loading: false,
    data: null,
  },
  customerAppts: {
    loading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  },
  appointmentSummaryCallerId: null,
  customerTransactions: null,
  shopSurveyInfo: null
};

export const Slice = createSlice({
  name: NAME_REDUCER,
  initialState,
  reducers: {
    setCurrentPage: (state, { payload }: { payload: number }) => {
      state.currentPage = payload;
    },
    setFullScreen: (state, { payload }: { payload: boolean }) => {
      state.currentPage = 1;
      state.fullScreen = payload;
      if (payload) {
        state.calendar.distanceTimeLine = 30;
      } else {
        state.calendar.distanceTimeLine = 15;
      }
    },
    activeTimeRow: (state, { payload }: { payload: string }) => {
      if (state.activeTimeRow === payload) {
        state.activeTimeRow = '';
        return;
      }
      state.activeTimeRow = payload;
    },
    upsertAppointment: (state, { payload }: { payload: IAppointmentResItem }) => {
      state.calendar.data = state.calendar.data.map(o => {
        if (o.id === payload.id?.toString()) {
          return mappingResToCalendarDataItem(payload);
        }
        return o;
      });
    },
    upsertAppointments: (state, { payload }: { payload: IAppointmentResItem[] }) => {
      const updatedApts: string[] = [];
      const calendarList = (state.calendar.data?.map(o => {
        const item = payload.find(s => s.id?.toString() === o.id?.toString());
        if (item) {
          const result = mappingResToCalendarDataItem(item);
          updatedApts.push(result.id);
          if (item?.strStatus !== 'CANCEL') {
            return result;
          } else {
            return null;
          }
        }
        return o;
      }));
      remove(calendarList, o => !o);
      state.calendar.data = (calendarList || []) as ICalendarItemData[];

      const newApts = payload.filter(o => !updatedApts.includes(o.id?.toString()));
      if (newApts?.length) {
        state.calendar.data = [...state.calendar.data, ...newApts.map(mappingResToCalendarDataItem)];
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(actions.getCallerIdHistory.fetch, (state, { payload }) => {
        state.callerId.loading = true;
        state.callerId.params = payload;
      })
      .addCase(actions.getCallerIdHistory.fail, (state) => {
        state.callerId.loading = false;
        state.callerId.data = null;
      })
      .addCase(actions.getCallerIdHistory.success, (state, { payload }) => {
        state.callerId.data = payload;
        state.callerId.loading = false;
      })
      // Caller V2 — pending caller list.
      .addCase(actions.getPendingCallers.fetch, (state) => {
        state.pendingCallers.loading = true;
      })
      .addCase(actions.getPendingCallers.fail, (state) => {
        state.pendingCallers.loading = false;
        state.pendingCallers.data = [];
      })
      .addCase(actions.getPendingCallers.success, (state, { payload }) => {
        state.pendingCallers.data = payload;
        state.pendingCallers.loading = false;
      })
      // Caller V2 — customer recent appointments (detail tab). Fetch resets prior
      // page data so a new caller never shows the previous customer's history.
      .addCase(actions.getCustomerRecent.fetch, (state) => {
        state.customerRecent.loading = true;
        state.customerRecent.data = null;
        state.customerAppts = { loading: false, data: [], total: 0, page: 1, pageSize: state.customerAppts.pageSize };
      })
      .addCase(actions.getCustomerRecent.fail, (state) => {
        state.customerRecent.loading = false;
        state.customerRecent.data = null;
      })
      .addCase(actions.getCustomerRecent.success, (state, { payload }) => {
        state.customerRecent.data = payload;
        state.customerRecent.loading = false;
      })
      // Caller V2 — paginated full appointment history (booking-history modal).
      .addCase(actions.getCustomerAppointments.fetch, (state, { payload }) => {
        state.customerAppts.loading = true;
        state.customerAppts.page = payload.page;
        state.customerAppts.pageSize = payload.pageSize;
      })
      .addCase(actions.getCustomerAppointments.fail, (state) => {
        state.customerAppts.loading = false;
      })
      .addCase(actions.getCustomerAppointments.success, (state, { payload }) => {
        state.customerAppts.data = payload.data;
        state.customerAppts.total = payload.total;
        state.customerAppts.loading = false;
      })
      .addCase(actions.getStaffByAppointment.success, (state, { payload }) => {
        state.staffSize = payload.total;
      })
      .addCase(actions.staffAvailableBooking.fetch, (state) => {
        state.staffAvailableBooking = [];
        state.staffAvailableBookingLoading = true;
      })
      .addCase(actions.staffAvailableBooking.fail, (state) => {
        state.staffAvailableBooking = [];
        state.staffAvailableBookingLoading = false;
      })
      .addCase(actions.staffAvailableBooking.success, (state, { payload }) => {
        state.staffAvailableBooking = payload;
        state.staffAvailableBookingLoading = false;
      })
      .addCase(actions.table.blockHours.success, (state, { payload }) => {
        state.table.blockHours = payload;
      })
      .addCase(actions.quickBooking.getServices.success, (state, { payload }) => {
        state.quickBooking.services = payload;
      })
      .addCase(actions.customer.clear, (state) => {
        state.customer = {
          data: [],
          params: {
            pageSize: TABLE_PAGE_SIZE,
          },
          total: 0,
          blockHours: [],
          appointmentSummaryCallerId: null
        };
      })
      .addCase(actions.customer.initData, (state, { payload }) => {
        const _params = {
          ...state.customer.params || {},
          ...payload || {},
        };
        if (_params.staffId === undefined) {
          delete _params.staffId;
        }

        if (_params.type === -1) {
          delete _params.type;
        }
        state.customer.data = [];
        state.customer.params = _params;
      })
      .addCase(actions.customer.setParams, (state, { payload }) => {
        const _params = {
          ...state.customer.params || {},
          ...payload || {},
        };

        if (_params.staffId === undefined) {
          delete _params.staffId;
        }

        if (_params.type === -1) {
          delete _params.type;
        }

        state.customer.params = _params;
      })
      .addCase(actions.customer.getData.success, (state, { payload }) => {
        state.customer.data = payload.data;
        state.customer.total = payload.total;
      })
      .addCase(actions.table.setParams, (state, { payload }) => {
        const _params = {
          ...state.table.params || {},
          ...payload || {},
        };

        if (_params.staffId === undefined) {
          delete _params.staffId;
        }

        if (_params.type === -1) {
          delete _params.type;
        }

        state.table.params = _params;
      })
      .addCase(actions.table.getData.success, (state, { payload }) => {
        state.table.data = payload.data;
        state.table.total = payload.total;
      })
      .addCase(actions.calendar.getData.success, (state, { payload }) => {
        state.calendar.data = payload;
      })
      .addCase(actions.calendar.setBlockHoursData, (state, { payload }) => {
        state.calendar.blockHours = payload;
      })
      .addCase(actions.prolongedTimeBlockHour, (state, { payload }) => {
        state.calendar.blockHours = state.calendar.blockHours.map(o => {
          if (o.id === payload.id) {
            const endTime = momentTimezone(o.endTime);
            if (payload.status === 'ADD') {
              endTime.add(15, 'minutes');
            } else {
              endTime.subtract(15, 'minutes');
            }
            return ({
              ...o,
              endTime: endTime.format('MM-DD-YYYY HH:mm:ss')
            });
          }
          return o;
        });
      })
      .addCase(actions.calendar.prolongedTime, (state, { payload }) => {
        state.calendar.data = state.calendar.data.map(o => {
          if (o.id === payload.id) {
            const endTime = momentTimezone(o.endTime);
            if (payload.status === 'ADD') {
              endTime.add(15, 'minutes');
            } else {
              endTime.subtract(15, 'minutes');
            }
            return ({
              ...o,
              endTime: endTime.format('MM-DD-YYYY HH:mm:ss')
            });
          }
          return o;
        });
      })
      .addCase(actions.calendar.updateDrop.fetch, (state, { payload }) => {
        state.calendar.data = state.calendar.data.map(o => {
          if (o.id === payload.id) {
            const endTime = momentTimezone(payload.startTime).add(payload.distance, 'minutes');
            return ({
              ...o,
              staffId: payload.staffId || o.staffId,
              startTime: payload.startTime,
              endTime: endTime.format('MM-DD-YYYY HH:mm:ss')
            });
          }
          return o;
        });
      })
      .addCase(actions.calendar.setDistanceTimeline, (state, { payload }) => {
        state.calendar.distanceTimeLine = payload;
      })
      .addCase(actions.calendar.setTypeView, (state, { payload }) => {
        state.calendar.typeView = payload;
      })
      .addCase(actions.calendar.setDateRanges, (state, { payload }) => {
        state.calendar.dateRanges = payload;
      })
      .addCase(actions.initData, (state) => {
        state.table = {
          data: [],
          params: {
            start_time: moment().format('YYYY-MM-DD'),
            end_time: moment().format('YYYY-MM-DD'),
            pageSize: TABLE_PAGE_SIZE,
          },
          total: 0,
          blockHours: [],
          appointmentSummaryCallerId: null
        };
      })
      .addCase(actions.getAppointmentSummaryCallerId.success, (state, { payload }) => {
        state.appointmentSummaryCallerId = payload;
      })
      .addCase(actions.getShopSurveyInfo.success, (state, { payload }) => {
        state.shopSurveyInfo = payload;
      })
      .addCase(actions.getCustomerTransactions.success, (state, { payload }) => {
        state.customerTransactions = payload;
      })
      ;
  },
});
export const aptUIActions = Slice.actions;
const aptReducer = Slice.reducer;
export default aptReducer;
